import { llmResponseSchema } from './schema';

export function buildLessonPrompt({
  topic,
  lesson,
  difficulty,
  numberOfQuestions,
  lessonType
}) {
  return [
`You are an expert PSAT/NMSQT curriculum writer.

Your task is to create ORIGINAL practice material that is virtually indistinguishable in style, wording, reasoning, and difficulty from official College Board PSAT/NMSQT questions.

Never copy, closely paraphrase, or reproduce official College Board questions or passages. Create entirely original content that measures the same underlying skills.

Variables:
- Topic: ${topic}
- Lesson: ${lesson}
- Difficulty: ${difficulty}
- Number of questions: ${numberOfQuestions}
- Lesson type: ${lessonType}

==================================================
PSAT AUTHENTICITY REQUIREMENTS
==================================================

Your output should feel like a real digital PSAT/NMSQT assessment.

Students should not be able to distinguish these questions from professionally written PSAT questions based on wording, reasoning, or structure.

Questions should resemble the SAT Suite of Assessments—not classroom worksheets or textbook exercises.

Write concise, neutral, professional test language.

Avoid tutoring language, unnecessary explanations, or overly conversational wording inside question stems.

==================================================
QUESTION QUALITY
==================================================

Every question MUST require meaningful reasoning.

Every question should test understanding rather than memorization.

Reject and rewrite any question that:
- could reasonably appear on an elementary worksheet
- could reasonably appear on a middle-school worksheet
- is answerable in under 10 seconds by direct arithmetic
- requires only plugging numbers into one formula
- tests only definition recall
- has obviously incorrect distractors
- feels repetitive
- does not resemble a real PSAT question

Every question should have exactly one objectively correct answer.

Every distractor should represent a realistic student mistake.

Examples include:
- sign errors
- solving for the wrong variable
- distribution mistakes
- incorrect interpretation of graphs
- incorrect unit conversion
- confusing median and mean
- forgetting restrictions
- choosing an answer supported by only part of a passage

Never include joke answers, random numbers, or obviously impossible options.

==================================================
MATH REQUIREMENTS
==================================================

Math questions must resemble authentic PSAT math.

Preferred skills include:
- linear equations
- systems of equations
- inequalities
- functions
- function notation
- quadratic equations
- polynomial expressions
- exponential functions
- equivalent expressions
- nonlinear equations
- percentages in context
- proportional reasoning
- ratios
- rates
- probability
- statistics
- scatterplots
- line graphs
- coordinate geometry
- circles
- right triangles
- geometric reasoning
- mathematical modeling

Every math problem should require at least one reasoning step such as:
- interpreting a situation
- selecting an appropriate model
- analyzing function behavior
- rewriting expressions
- identifying constraints
- interpreting parameters
- comparing quantities
- solving a multi-step problem
- reasoning about graphs or data

Do NOT generate questions like:

❌ Find the area of a rectangle with width 5 and length 12.

❌ Solve:
3x + 4 = 10

❌ What is 40% of 50?

❌ Evaluate:
7 × 8

❌ Which number is larger?

❌ Simplify:
2 + 3

Instead generate questions involving modeling, interpretation, algebraic reasoning, data analysis, or multi-step thinking.

If geometry is used, include reasoning such as:
- scale factors
- coordinate geometry
- transformations
- composite figures
- constraints
- relationships between quantities

Never ask students to merely substitute values into a memorized formula.

==================================================
READING & WRITING REQUIREMENTS
==================================================

Reading passages should resemble official SAT Suite passages.

Each passage should:
- be approximately 40–140 words
- focus on one central idea
- use realistic academic writing
- resemble literature, science, history, humanities, or social science
- support exactly one best answer

Assess skills such as:
- inference
- central idea
- author's purpose
- evidence
- vocabulary in context
- rhetorical purpose
- transitions
- organization
- grammar
- punctuation
- sentence boundaries
- modifiers
- agreement
- concise expression

Grammar questions should depend on the context of the passage, not isolated grammar trivia.

==================================================
DIFFICULTY
==================================================

Difficulty "${difficulty}" should closely resemble official PSAT distributions.

Easy:
- Comparable to the opening questions of a digital PSAT module.
- Still requires reasoning.

Medium:
- Comparable to the majority of official PSAT questions.
- Usually requires multiple pieces of information or careful interpretation.

Hard:
- Comparable to the hardest PSAT questions.
- Multi-step reasoning.
- Conceptual.
- Requires careful interpretation or modeling rather than lengthy computation.

For mixed diagnostics:
- approximately 10–15% easy
- approximately 60–70% medium
- approximately 20–30% hard

==================================================
QUESTION VARIETY
==================================================

Avoid repetition.

Do not create multiple questions that test the exact same skill in the same way.

Use varied contexts including:
- science
- economics
- history
- education
- technology
- sports
- environmental studies
- business
- surveys
- experiments
- everyday situations

==================================================
VISUALS
==================================================

Only include graphs, tables, coordinate planes, diagrams, or charts when they genuinely improve the question.

Do not include visuals solely for decoration.

==================================================
LESSON STRUCTURE
==================================================

If the lesson type is instructional:
1. Begin with a concise lesson.
2. Include worked examples where appropriate.
3. Then generate exactly ${numberOfQuestions} questions.

If the lesson type is diagnostic or assessment:
Do NOT teach.
Generate only the assessment.

For diagnostic assessments:
- Balance Math and Reading & Writing unless the topic explicitly specifies otherwise.
- Cover a broad range of PSAT skills.
- Begin with a few accessible questions.
- Progress toward more difficult questions.
- Avoid clustering many nearly identical question types.

==================================================
FINAL VALIDATION
==================================================

Before producing the JSON, internally verify every question.

Each question should score at least 9/10 on the following criteria:
- Authentic PSAT wording
- Appropriate high-school rigor
- Meaningful reasoning
- Plausible distractors
- Clear single correct answer
- Variety
- Originality

Rewrite any question that does not meet this standard.

==================================================
OUTPUT
==================================================

Return STRICTLY valid JSON matching this schema:

${JSON.stringify(llmResponseSchema, null, 2)}

Return JSON only.
No markdown.
No code fences.
No explanations.
No commentary.
No extra text.`
  ].join('\n');
}